import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ExternalLink, Send, X } from "lucide-react";

const CATEGORIES = ["All", "Solid Extrusions", "Sponge Extrusions", "Reinforced Hoses", "Molded Products"];

function QuoteDialog({ product, onClose }) {
  const [qty, setQty] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/portal/enquiry", { product: product.name, details: [qty && `Quantity: ${qty}`, details].filter(Boolean).join("\n") });
      toast.success(r.data.message);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="quote-dialog">
        <h3 className="font-display font-bold text-slate-900">Request a Quote</h3>
        <p className="text-xs text-slate-500 mt-1 mb-4">{product.name}</p>
        <input className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mb-3" placeholder="Quantity / size required (e.g. 500 m, ID 8mm)"
               value={qty} onChange={(e) => setQty(e.target.value)} data-testid="quote-qty" />
        <textarea className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" rows={3} placeholder="Any specific requirements (optional)"
                  value={details} onChange={(e) => setDetails(e.target.value)} data-testid="quote-details" />
        <div className="flex gap-2 mt-4">
          <button onClick={submit} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-md py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="quote-submit">
            <Send size={13} /> {busy ? "Sending…" : "Send Request"}
          </button>
          <button onClick={onClose} className="px-4 border border-slate-200 rounded-full text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ProductDetail({ product, onClose, onQuote, onPrev, onNext }) {
  const [imgIdx, setImgIdx] = useState(0);
  useEffect(() => { setImgIdx(0); }, [product.id]);
  const img = product.images?.[imgIdx];
  return (
    <div className="fixed inset-0 z-50 bg-[#3B82F6]/60 backdrop-blur-md flex items-center justify-center p-2 sm:p-6" onClick={onClose} data-testid="product-detail">
      <div className="bg-[#FBFAF8] rounded-3xl w-full max-w-[1240px] h-[94vh] lg:h-[88vh] overflow-hidden relative flex flex-col fade-up" onClick={(e) => e.stopPropagation()}>
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-slate-200/70 flex-none">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FC5F5F]">{product.category}</div>
          <div className="flex items-center gap-2">
            <button onClick={onPrev} className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-[#3B82F6] hover:border-[#3B82F6]/40 transition-colors duration-200" data-testid="detail-prev"><ArrowLeft size={15} /></button>
            <button onClick={onNext} className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-[#3B82F6] hover:border-[#3B82F6]/40 transition-colors duration-200" data-testid="detail-next"><ArrowRight size={15} /></button>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#3B82F6] text-white flex items-center justify-center hover:bg-[#2563EB] transition-colors duration-200 ml-1" data-testid="detail-close"><X size={15} /></button>
          </div>
        </div>

        <div className="flex-1 min-h-0 grid lg:grid-cols-[1.05fr_1fr]">
          {/* Gallery */}
          <div className="p-6 sm:p-10 flex flex-col min-h-0 max-lg:max-h-[42vh]">
            <div className="flex-1 min-h-0 rounded-3xl bg-slate-100 border border-slate-100 flex items-center justify-center overflow-hidden relative">
              {img ? <img key={img} src={img} alt={product.name} className="w-full h-full object-contain p-8 fade-up" /> :
                <span className="text-slate-300 text-sm">No image</span>}
              {product.images?.length > 1 && (
                <>
                  <button onClick={() => setImgIdx((imgIdx - 1 + product.images.length) % product.images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow-md flex items-center justify-center text-slate-600 hover:text-slate-900 hover:scale-105 transition-transform duration-200"><ArrowLeft size={16} /></button>
                  <button onClick={() => setImgIdx((imgIdx + 1) % product.images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow-md flex items-center justify-center text-slate-600 hover:text-slate-900 hover:scale-105 transition-transform duration-200"><ArrowRight size={16} /></button>
                  <div className="absolute bottom-4 right-5 text-[11px] font-semibold bg-white/90 rounded-full px-2.5 py-1 text-slate-500">{imgIdx + 1} / {product.images.length}</div>
                </>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-3 mt-5 flex-none">
                {product.images.map((im, i) => (
                  <button key={im} onClick={() => setImgIdx(i)}
                          className={`w-20 h-20 rounded-2xl overflow-hidden border-2 bg-white transition-colors duration-200 ${i === imgIdx ? "border-[#3B82F6]" : "border-transparent hover:border-slate-300"}`} data-testid={`detail-thumb-${i}`}>
                    <img src={im} alt="" className="w-full h-full object-contain p-2" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="min-h-0 overflow-y-auto scroll-thin px-6 sm:px-10 lg:pl-2 lg:pr-12 py-6 sm:py-10">
            <h2 className="font-display text-3xl sm:text-[40px] font-light text-slate-900 tracking-tight leading-[1.1]" data-testid="detail-title">{product.name}</h2>
            <p className="text-[15px] text-slate-500 leading-7 mt-5">{product.description}</p>

            {product.specs?.length > 0 && (
              <div className="mt-9">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400">Specifications</div>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="rounded-2xl bg-white border border-slate-200/80 divide-y divide-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
                  {product.specs.map((s) => (
                    <div key={s.label} className="flex items-baseline justify-between gap-6 px-5 py-3.5 text-sm">
                      <span className="text-[11px] uppercase tracking-wider text-slate-400 font-medium whitespace-nowrap">{s.label}</span>
                      <span className="text-slate-700 text-right font-medium">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {product.features?.length > 0 && (
              <div className="mt-9">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400">Key Features</div>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="grid sm:grid-cols-1 gap-2.5">
                  {product.features.map((f) => (
                    <div key={f} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                      <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-none mt-0.5"><Check size={11} strokeWidth={3} /></span>
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {product.applications?.length > 0 && (
              <div className="mt-9">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400">Applications</div>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.applications.map((a) => (
                    <span key={a} className="text-xs px-3.5 py-1.5 rounded-full bg-slate-100 text-slate-600 border border-slate-100">{a}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="h-24" />
          </div>
        </div>

        {/* CTA bar */}
        <div className="flex-none border-t border-slate-200/70 bg-white/90 backdrop-blur px-6 sm:px-10 py-4 flex flex-wrap items-center gap-3">
          <div className="hidden sm:block text-xs text-slate-400 mr-auto">ISO 9001:2015 certified · Custom sizes & colors available</div>
          {product.source_url && (
            <a href={product.source_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#3B82F6] border border-slate-200 rounded-md px-5 py-2.5 transition-colors duration-200" data-testid="detail-site-link">
              View on Website <ExternalLink size={13} />
            </a>
          )}
          <button onClick={onQuote} className="inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-md px-8 py-3 text-sm font-semibold transition-colors duration-200 shadow-[0_8px_24px_rgba(59,130,246,0.3)]" data-testid="detail-quote-btn">
            Request Quote <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PortalCatalogue() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [openId, setOpenId] = useState(null);
  const [quoteFor, setQuoteFor] = useState(null);

  useEffect(() => { api.get("/portal/catalogue/products").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const filtered = useMemo(() => (cat === "All" ? items : items.filter((p) => p.category === cat)), [items, cat]);
  const openIdx = filtered.findIndex((p) => p.id === openId);
  const open = openIdx >= 0 ? filtered[openIdx] : null;

  return (
    <div className="space-y-8" data-testid="portal-catalogue">
      <div className="fade-up">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FC5F5F]">Product Catalogue</div>
        <h1 className="text-3xl sm:text-4xl font-display font-light text-slate-900 tracking-tight mt-1">Premium Silicone Products</h1>
        <p className="text-sm text-slate-500 mt-1.5 max-w-xl">Explore our full range of extrusions, hoses and molded products — open any product for details and request a quote in one click.</p>
      </div>

      <div className="flex flex-wrap gap-2 fade-up" style={{ animationDelay: "60ms" }}>
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`cat-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-2 text-xs font-semibold rounded-full border transition-colors duration-200 ${cat === c ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white text-slate-500 border-slate-200 hover:border-[#3B82F6]/40 hover:text-[#3B82F6]"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 fade-up" style={{ animationDelay: "120ms" }}>
        {filtered.map((p) => (
          <button key={p.id} onClick={() => setOpenId(p.id)} data-testid={`catalogue-card-${p.id}`}
                  className="text-left bg-white border border-slate-200 rounded-2xl overflow-hidden group hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300">
            <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
              {p.images?.[0] ? (
                <img src={p.images[0]} alt={p.name} loading="lazy" className="w-full h-full object-contain p-5 group-hover:scale-105 transition-transform duration-300" />
              ) : <span className="text-slate-300 text-xs">No image</span>}
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-[#FC5F5F] font-semibold">{p.category}</div>
              <div className="text-sm font-semibold text-slate-800 mt-1 leading-snug">{p.name}</div>
              <div className="mt-2.5 text-[11px] text-slate-400 inline-flex items-center gap-1 group-hover:text-[#3B82F6] transition-colors duration-200">View details <ArrowRight size={11} /></div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-sm text-slate-400 text-center py-12">No products in this category yet.</div>}
      </div>

      {open && (
        <ProductDetail
          product={open}
          onClose={() => setOpenId(null)}
          onQuote={() => setQuoteFor(open)}
          onPrev={() => setOpenId(filtered[(openIdx - 1 + filtered.length) % filtered.length].id)}
          onNext={() => setOpenId(filtered[(openIdx + 1) % filtered.length].id)}
        />
      )}
      {quoteFor && <QuoteDialog product={quoteFor} onClose={() => setQuoteFor(null)} />}
    </div>
  );
}
